import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardMedia,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useRef, useState } from "react";
import { getStatusOptions, getStatusOptionsArray } from "../../utils/utils";
import { useTheme } from "@emotion/react";
import { useThemeContext } from "../../context/themeContext";
import { toast } from "react-toastify";
import PlaceholderPoster from "../../components/PlaceholderPoster";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import PosterDark from "../../assets/activity-tutorial-dark.jpg";
import PosterLight from "../../assets/activity-tutorial-light.jpg";
import LoopyArrow from "../../components/LoopyArrow";
import { useMutation } from "@tanstack/react-query";
import useApi from "../../hooks/useApi";

export default function ActivityTutorial({ onDismiss }) {
  const { isTouchDevice } = useThemeContext();
  const { editUiState } = useApi();
  const content_title = "Track content shortcut";

  const theme = useTheme();
  const isBelowSm = useMediaQuery(theme.breakpoints.down("sm"));
  const statusOptions = getStatusOptions(theme);
  const posterActions = getStatusOptionsArray(theme).filter(
    (option) => option.value !== "none",
  );
  const [showPosterOverlay, setShowPosterOverlay] = useState(false);
  const timerRef = useRef(null); // timer for long press
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [posterStatus, setPosterStatus] = useState("none");

  const handlePosterClick = () => {
    toast.info(
      `👌 ${
        isTouchDevice ? "Long press" : "Click on"
      } any real poster to see more details!`,
    );
  };

  const handleTouchStart = (event) => {
    timerRef.current = setTimeout(() => {
      setShowPosterOverlay(false);
      handlePosterClick();
    }, 300); // long-press threshold in ms
  };

  const handleTouchEnd = () => {
    clearTimeout(timerRef.current);
  };

  const handleTouchMove = () => {
    clearTimeout(timerRef.current);
    setShowPosterOverlay(false);
  };

  const onTap = () => {
    setShowPosterOverlay(!showPosterOverlay);
  };

  const handlePosterActionClick = (status) => {
    if (status === posterStatus) {
      setPosterStatus("none");
      toast.success("Tracking removed");
    } else {
      setPosterStatus(status);
      toast.success(`Content marked as ${statusOptions[status]?.label.toLowerCase()}`);
    }
  };

  const dismissMutation = useMutation({
    mutationFn: editUiState,
    onSuccess: (data) => {
      onDismiss?.();
    },
  });

  const handleDismiss = () => {
    const key = isTouchDevice
      ? "show-status-tracker-helper-mobile"
      : "show-status-tracker-helper-desktop";
    dismissMutation.mutate({
      key,
      value: false,
    });
  };

  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: "sm",
        display: "flex",
        backgroundImage: "none",
        flexDirection: { xs: "column", sm: "row" },
        boxShadow: "none",

        pb: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
        }}
      >
        <Box
          onClick={isTouchDevice ? onTap : handlePosterClick}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchMove}
          onMouseEnter={() => setShowPosterOverlay(true)}
          onMouseLeave={() => setShowPosterOverlay(false)}
          sx={{
            position: "relative",
            width: "33%",
            maxWidth: { xs: "70px", sm: "120px" },
            flexBasis: "30%",
            flexShrink: 0,
            borderRadius: 1,
          }}
        >
          <CardActionArea>
            {" "}
            {!imageFailed ? (
              <Box position="relative">
                {!imageLoaded && (
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{
                      display: "block",
                      paddingTop: "150%",
                      borderRadius: 1,
                    }}
                  />
                )}
                <CardMedia
                  component="img"
                  image={
                    theme.palette.mode === "dark" ? PosterDark : PosterLight
                  }
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageFailed(true)}
                  sx={{
                    width: "100%",
                    borderRadius: 1,
                    objectFit: "contain",
                    border:
                      posterStatus !== "none"
                        ? `3px solid ${theme.palette.status[posterStatus].main}`
                        : "none",
                  }}
                />
                <Box
                  position="absolute"
                  bottom={isBelowSm ? -70 : -20}
                  right={isBelowSm ? 90 : -25}
                  sx={{
                    transform: isBelowSm
                      ? "translateX(100%)"
                      : "translateX(80%)",
                  }}
                >
                  <LoopyArrow />
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  border:
                    posterStatus !== "none"
                      ? `3px solid ${theme.palette.status[posterStatus].main}`
                      : "none",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <PlaceholderPoster />
              </Box>
            )}
          </CardActionArea>
          {showPosterOverlay && (
            <Stack
              direction="column"
              spacing={0}
              sx={{
                position: "absolute",
                top: 3,
                right: 3,
                alignItems: "center",
                backgroundColor: theme.palette.background.paper,
                borderRadius: 1,
                padding: 0.5,
              }}
            >
              {posterActions.length > 0 &&
                posterActions.map((option) => (
                  <Tooltip
                    placement="left"
                    title={
                      posterStatus === option.value
                        ? option.label
                        : option.action
                    }
                    key={option.value}
                  >
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePosterActionClick(option.value);
                      }}
                      sx={{ p: 0.5 }}
                    >
                      {posterStatus === option.value
                        ? option.iconFilled
                        : option.icon}
                    </IconButton>
                  </Tooltip>
                ))}
            </Stack>
          )}
        </Box>
        <Box
          sx={{
            pl: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flexBasis: "70%",
            flexGrow: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                verticalAlign: "middle",
                lineHeight: 0,
                mr: 1,
                // size the icon to match the text + nudge so its optical
                // centre lines up with the cap-height middle of the first line
                "& .MuiSvgIcon-root": {
                  fontSize: "1.3em",
                  transform: "translateY(-0.05em)",
                },
              }}
            >
              <TipsAndUpdatesIcon />
            </Box>
            {content_title}
          </Typography>
          <Divider />
          <Box component="ul" sx={{ pl: 2, mt: 0, mb: 0 }}>
            <Typography component="li">
              {isTouchDevice ? "Tap on" : "Hover over"} the poster to reveal
              status tracking
            </Typography>
            <Typography component="li">
              {isTouchDevice ? "Long press" : "Click on"} the poster to see more
              details
            </Typography>
            <Typography component="li">
              Tracked content is outlined with its&nbsp;
              <Box
                component="span"
                sx={{
                  fontWeight: posterStatus !== "none" ? "bold" : "inherit",
                  color:
                    posterStatus !== "none"
                      ? statusOptions[posterStatus].color
                      : "inherit",
                }}
              >
                status colour
              </Box>
            </Typography>
          </Box>
          <Button
            variant="outlined"
            sx={{ alignSelf: "flex-end", width: 75, mt: 1 }}
            onClick={handleDismiss}
          >
            Got it
          </Button>
        </Box>
      </Box>
    </Card>
  );
}
